import { EndpointDefinition } from '../../../core/models/endpoint-catalog.model';

export interface PriceBreakdownLine {
  param: string;
  label: string;
  cents: number;
}

/**
 * `baseCents` (the endpoint's `credits_cents`) and `surchargeCents` (the
 * sum of whichever params' `surcharge_cents` are active) are already the
 * same unit, so `totalCents` is just their sum no wallet/exchange-rate
 * lookup needed the way an earlier `credits`-denominated base cost would
 * have required.
 */
export interface PriceEstimate {
  baseCents: number;
  surchargeCents: number;
  totalCents: number;
  breakdown: PriceBreakdownLine[];
}

const EMPTY_ESTIMATE: PriceEstimate = { baseCents: 0, surchargeCents: 0, totalCents: 0, breakdown: [] };

/** Round to 4 decimal places surcharge_cents isn't always a whole cent
 *  (e.g. 0.01 = a hundredth of a cent), and summing several fractional
 *  values can otherwise leave float-precision noise like
 *  0.30000000000000004 in what gets displayed. */
function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

export function estimateEndpointPrice(
  endpoint: EndpointDefinition | undefined,
  activeParamNames: Iterable<string>,
): PriceEstimate {
  if (!endpoint) return EMPTY_ESTIMATE;

  const active = activeParamNames instanceof Set ? activeParamNames : new Set(activeParamNames);
  const chargedGroups = new Set<string>();
  const breakdown: PriceBreakdownLine[] = [];
  let surchargeCents = 0;

  for (const param of endpoint.params) {
    if (!param.surcharge_cents || !active.has(param.name)) continue;

    const group = param.surcharge_group ?? param.name;
    if (chargedGroups.has(group)) continue;
    chargedGroups.add(group);

    surchargeCents += param.surcharge_cents;
    breakdown.push({ param: param.name, label: param.description ?? param.name, cents: param.surcharge_cents });
  }

  surchargeCents = round4(surchargeCents);
  const baseCents = endpoint.credits_cents;

  return { baseCents, surchargeCents, totalCents: round4(baseCents + surchargeCents), breakdown };
}
